// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import axios from 'axios';
import * as Constants from '../../constants/metrics';
import actionParams from '../../utils/analyticsActionParams';
import { getConfig, getUserActiveOrgUuid, getUserContexts } from '../../reducers/portalConfig';
import { get } from 'lodash';
import { stringify } from 'query-string';

export const fetchMetricsData = (
    chartID,
    params,
    metric,
    aggregation,
    type,
  ) =>
  async (dispatch, getState) => {
    const apiParams = actionParams(params);
    const metricType = params.metricType;
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/analytics/metrics/v1/${metric}/${type}/${aggregation}${apiParams}`;
    const activeOrgUuid = getUserActiveOrgUuid(getState());
    const headers = activeOrgUuid ? { 'APIM-OrgUuid': activeOrgUuid } : {};

    dispatch({
      chartID,
      metricType,
      type: Constants.METRICS_DATA_REQUEST,
    });

    await axios.get(url, { credentials: 'include', headers })
      .then((response) => {
        dispatch({
          chartID,
          metricType,
          type: Constants.METRICS_DATA_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          chartID,
          metricType,
          type: Constants.METRICS_DATA_ERROR,
          payload: response.data,
        }),
      );
  };

export const fetchApiList = ( { name = null,
    status = null,
    page = 0,
    size = 50 } ) =>
    async (dispatch, getState) => {
      const config = getConfig(getState());
      const { portal } = config;
      const params = stringify(
        {
          ...(name && { name }),
          ...(status && { status }),
          page,
          size,
        },
      );
      const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/apis?${params}`;
  
      dispatch({
        type: Constants.METRICS_API_LIST_REQUEST,
      });
  
      await axios.get(url, { credentials: 'include' })
        .then((response) => {
          dispatch({
            type: Constants.METRICS_API_LIST_SUCCESS,
            payload: response.data,
          });
        })
        .catch((response) =>
          dispatch({
            type: Constants.METRICS_API_LIST_ERROR,
            payload: response.data,
          }),
        );
  };

export const fetchApiPerOrgList = () =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/internal/ratequotasummary/apiperorg?size=2000`;

    dispatch({
      type: Constants.METRICS_API_PER_ORG_LIST_REQUEST,
    });

    await axios.get(url, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_API_PER_ORG_LIST_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_API_PER_ORG_LIST_ERROR,
          payload: response.data,
        }),
      );
  };

export const fetchApisWithRLQ = () =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/internal/ratequotasummary/apis?size=2000`;

    dispatch({
      type: Constants.METRICS_APIS_WITH_RLQ_LIST_REQUEST,
    });

    await axios.get(url, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_APIS_WITH_RLQ_LIST_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_APIS_WITH_RLQ_LIST_ERROR,
          payload: response.data,
        }),
      );
  };

export const fetchApiKeyList = () =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/admin/api-management/internal/applications/apiKeys`;

    dispatch({
      type: Constants.METRICS_API_KEY_LIST_REQUEST,
    });

    await axios.get(url, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_API_KEY_LIST_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_API_KEY_LIST_ERROR,
          payload: response.data,
        }),
      );
  };

  export const fetchAppListForOrgInContext = () =>
    async (dispatch, getState) => {
      const config = getConfig(getState());
      const { portal } = config;
      const url = `${portal.hostname}/admin/api-management/internal/applications/applicationApiOrgXref`;
  
      dispatch({
        type: Constants.METRICS_APP_LIST_REQUEST,
      });
  
      await axios.get(url, { credentials: 'include' })
        .then((response) => {
          dispatch({
            type: Constants.METRICS_APP_LIST_SUCCESS,
            payload: response.data,
          });
        })
        .catch((response) =>
          dispatch({
            type: Constants.METRICS_APP_LIST_ERROR,
            payload: response.data,
          }),
        );
    };

export const fetchAppList = ({ name = null,
    status = null,
    page = 0,
    size = 50 }) =>
    async (dispatch, getState) => {
      const config = getConfig(getState());
      const { portal } = config;
      const params = stringify(
        {
          ...(name && { name }),
          ...(status && { status }),
          page,
          size,
        },
      );
      const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/applications?${params}`;
  
      dispatch({
        type: Constants.METRICS_APP_LIST_REQUEST,
      });
  
      await axios.get(url, { credentials: 'include' })
        .then((response) => {
          dispatch({
            type: Constants.METRICS_APP_LIST_SUCCESS,
            payload: response.data,
          });
        })
        .catch((response) =>
          dispatch({
            type: Constants.METRICS_APP_LIST_ERROR,
            payload: response.data,
          }),
        );
    };

export const fetchOrgList = ( {
    name = null,
    status = null,
    page = 0,
    size = 50,
  } = {} ) =>
    async (dispatch, getState) => {
      const config = getConfig(getState());
      const { portal } = config;
      const params = stringify({
        ...(name && { name }),
        ...(status && { status }),
        page,
        size,
      }, true);
      const url = `${portal.hostname}/api/${portal.tenantPrefix}/tenant-admin/1.0/organizations?${params}`;
  
      dispatch({
        type: Constants.METRICS_ORG_LIST_REQUEST,
      });
  
      await axios.get(url, { credentials: 'include' })
        .then((response) => {
          dispatch({
            type: Constants.METRICS_ORG_LIST_SUCCESS,
            payload: response.data,
          });
        })
        .catch((response) =>
          dispatch({
            type: Constants.METRICS_ORG_LIST_ERROR,
            payload: response.data,
          }),
        );
  };

export const setChartParams = (chartId, params) =>
  ({ type: Constants.SET_CHART_STATE, chartId, params });

export const fetchCustomChartData = (body, chartID) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/analytics/metrics/v1/_query`;
    const userContexts = getUserContexts(getState());
    const activeOrgUuid = get(userContexts, 'userContexts[0].activeOrgUuid');
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(activeOrgUuid ? {
        'APIM-OrgUuid': activeOrgUuid,
      } : {}),
    };

    dispatch({
      chartID,
      type: Constants.METRICS_CUSTOM_CHART_DATA_REQUEST,
    });

    await axios.post(url, body, { credentials: 'include', headers })
      .then((response) => {
        dispatch({
          chartID,
          type: Constants.METRICS_CUSTOM_CHART_DATA_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          chartID,
          type: Constants.METRICS_CUSTOM_CHART_DATA_ERROR,
          payload: response.data,
        }),
      );
  };

export const fetchGatewayList = () =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/deployments/1.0/proxies`;

    dispatch({
      type: Constants.METRICS_GATEWAY_LIST_REQUEST,
    });

    await axios.get(url, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_GATEWAY_LIST_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_GATEWAY_LIST_ERROR,
          payload: response.data,
        }),
      );
  };

export const fetchCustomReports = () =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/analytics/metrics/v1/reports`;
    const userContexts = getUserContexts(getState());
    const activeOrgUuid = get(userContexts, 'userContexts[0].activeOrgUuid');
    const headers = {
      ...(activeOrgUuid ? {
        'APIM-OrgUuid': activeOrgUuid,
      } : {}),
    };

    dispatch({
      type: Constants.METRICS_CUSTOM_REPORT_REQUEST,
    });

    await axios.get(url, { credentials: 'include', headers })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_ERROR,
          payload: response.data,
        }),
      );
  };

export const saveCustomReport = (body) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/analytics/metrics/v1/reports`;
    const userContexts = getUserContexts(getState());
    const activeOrgUuid = get(userContexts, 'userContexts[0].activeOrgUuid');

    dispatch({
      type: Constants.METRICS_CUSTOM_REPORT_CREATE_REPORT_REQUEST,
    });

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(activeOrgUuid ? {
        'APIM-OrgUuid': activeOrgUuid,
      } : {}),
    };

    await axios.post(url, body, { credentials: 'include', headers })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_CREATE_REPORT_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_CREATE_REPORT_ERROR,
          payload: response.data,
        }),
      );
  };

export const saveChartPanel = (body, reportId) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/analytics/metrics/v1/reports/${reportId}/panels`;
    const userContexts = getUserContexts(getState());
    const activeOrgUuid = get(userContexts, 'userContexts[0].activeOrgUuid');

    dispatch({
      type: Constants.METRICS_CUSTOM_REPORT_CREATE_CHART_REQUEST,
    });

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(activeOrgUuid ? {
        'APIM-OrgUuid': activeOrgUuid,
      } : {}),
    };

    await axios.post(url, body, { credentials: 'include', headers })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_CREATE_CHART_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_CREATE_CHART_ERROR,
          payload: response.data,
        }),
      );
  };

export const fetchChartPanels = (reportId) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/analytics/metrics/v1/reports/${reportId}/panels`;
    const userContexts = getUserContexts(getState());
    const activeOrgUuid = get(userContexts, 'userContexts[0].activeOrgUuid');
    const headers = {
      ...(activeOrgUuid ? {
        'APIM-OrgUuid': activeOrgUuid,
      } : {}),
    };

    dispatch({
      type: Constants.METRICS_CUSTOM_REPORT_GET_PANELS_REQUEST,
    });

    await axios.get(url, { credentials: 'include', headers })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_GET_PANELS_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_GET_PANELS_ERROR,
          payload: response.data,
        }),
      );
  };

export const editChartPanel = (body, reportId, panelId) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/analytics/metrics/v1/reports/${reportId}/panels/${panelId}`;
    const userContexts = getUserContexts(getState());
    const activeOrgUuid = get(userContexts, 'userContexts[0].activeOrgUuid');

    dispatch({
      type: Constants.METRICS_CUSTOM_REPORT_EDIT_PANEL_REQUEST,
    });

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(activeOrgUuid ? {
        'APIM-OrgUuid': activeOrgUuid,
      } : {}),
    };

    await axios.put(url, body, { credentials: 'include', headers })
      .then((response) => {
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_EDIT_PANEL_SUCCESS,
          payload: response.data,
        });
      })
      .catch((response) =>
        dispatch({
          type: Constants.METRICS_CUSTOM_REPORT_EDIT_PANEL_ERROR,
          payload: response.data,
        }),
      );
  };
